import React, { ChangeEvent, FC, MouseEvent, useContext, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Divider,
  FormControlLabel,
  FormGroup,
  Step,
  StepContent,
  StepLabel,
  Stepper,
  Switch,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useTheme,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Check } from '@mui/icons-material';
import SurveyContactData from '../../../../components/SurveyContactData/SurveyContactData';
import DesignChanger from '../../../../components/DesignChanger/DesignChanger';
import { ReactComponent as FlagDe } from '../../../../assets/images/flags/de.svg';
import { ReactComponent as FlagGb } from '../../../../assets/images/flags/gb.svg';
import { ReactComponent as FlagNl } from '../../../../assets/images/flags/nl.svg';
import { IDesign } from '../../../../shared/interfaces/IDesign';
import { IContactData } from '../../../../shared/interfaces/IContactData';
import SurveyService from '../../../../services/SurveyService';
import { AuthContext } from '../../../../providers/AuthProvider';
import { useNavigate } from 'react-router-dom';
import { ICustomSurveyEntry } from '../../../../shared/interfaces/ICustomSurveyEntry';
import SurveyUtils from '../../../../shared/SurveyUtils';

const defaultDesign: IDesign = {
  companyLogo: '',
  customFont: '',
  customFontFormat: '',
  primaryColor: '#357E8B',
  secondaryColor: '#000000',
};

const defaultContactData = {
  contactEmail: '',
  imprintLink: '',
  privacyPolicyLink: '',
};

const defaultSurveyData = {
  id: '',
  archived: false,
  archivedAt: '',
  createdAt: '',
  updatedAt: '',
  companyName: '',
  companyWebsiteUrl: '',
  contactForm: false,
  customFont: '',
  customFontFormat: '',
  label: '',
  lang: 'de',
  note: '',
  outroText: 'Vielen Dank für Ihre Teilnahme an unserer Umfrage!',
  slug: '',
  title: 'Individuelle Umfrage',
  introText: 'Willkommen zu unserer Umfrage!',
  type: 'CUSTOMSURVEY',
};

interface Props {
  id?: string;
  loadSurveyType?: string;
  isEdit?: boolean;
  reload: () => Promise<void>;
}

const CustomSurveyGuid: FC<Props> = ({ id, isEdit, reload, loadSurveyType }) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { hasRole } = useContext(AuthContext);

  const [loading, setLoading] = useState<boolean>(false);

  const [currentSurveyGuideStep, setCurrentSurveyGuideStep] = useState<number>(0);
  const [canEditDesign, setCanEditDesign] = useState<boolean>(false);
  const [showContactData, setShowContactData] = useState<boolean>(defaultSurveyData.contactForm);
  const [initialized, setInitialized] = useState<boolean>(false);

  const [design, setDesign] = useState<IDesign>(defaultDesign);
  const [contactData, setContactData] = useState<IContactData>(defaultContactData);
  const [surveyData, setSurveyData] = useState<ICustomSurveyEntry>(defaultSurveyData);
  const [showUrlError, setShowUrlError] = useState<boolean>(false);
  const [isCheckboxChecked, setIsCheckboxChecked] = useState(false);
  const [surveyID, setSurveyID] = useState<string>(id || '');

  const handleNextSurveyGuideStep = () => {
    setCurrentSurveyGuideStep((prevSurveyGuideStep) => prevSurveyGuideStep + 1);
  };

  const handlePreviousSurveyGuideStep = () => {
    setCurrentSurveyGuideStep((prevSurveyGuideStep) => prevSurveyGuideStep - 1);
  };

  const handleAddSurvey = async () => {
    const getMergedState = () => ({
      ...surveyData,
      ...contactData,
      ...design,
    });
    const mergedState: any = getMergedState();
    if (isEdit) {
      if (!id) return;
      await SurveyService.updateCustomsurvey(id, mergedState);
    } else {
      mergedState.slug = '';
      await SurveyService.addCustomsurvey(mergedState);
    }
    await reload();
  };

  const isValidCompanyUrl = (url: string): boolean => {
    const validEndings = ['.com', '.de', '.org', '.net', '.info', '.at', '.eu', '.ch'];
    const pattern = /^(https?:\/\/)?(www\.)?[a-zA-Z0-9-]+(\.[a-zA-Z0-9-]+)+$/;
    return pattern.test(url) && validEndings.some((ending) => url.endsWith(ending));
  };

  const handleOutroTextChange = (companyName: string) => {
    const value = surveyData.outroText.replace(/\[Firmenname\]/, companyName || '[Firmenname]');
    setSurveyData((prevState) => ({
      ...prevState,
      outroText: value,
    }));
  };

  useEffect(() => {
    if (!hasRole('can-use-customsurvey')) {
      navigate('/');
    }
  }, [hasRole, navigate]);

  useEffect(() => {
    if (!surveyID) return;

    const controller = new AbortController();
    const signal = controller.signal;

    const getSurvey = async () => {
      setLoading(true);
      try {
        const endpointPath = SurveyUtils.getEndpointByType(loadSurveyType);
        const survey = await SurveyService.getSurveyPublic(
          surveyID,
          endpointPath,
          setLoading,
          signal
        );
        if (!survey) {
          setLoading(false);
          return;
        }
        if (survey.type === 'CUSTOMSURVEY') {
          setSurveyData(survey);
        }
        setDesign({
          companyLogo: survey.companyLogo,
          primaryColor: survey.primaryColor,
          secondaryColor: survey.secondaryColor,
          customFont: survey.customFont,
          customFontFormat: survey.customFontFormat,
        });
        setContactData({
          privacyPolicyLink: survey.privacyPolicyLink,
          contactEmail: survey.contactEmail,
          imprintLink: survey.imprintLink,
        });
        setIsCheckboxChecked(survey.feedbackActive);
        setShowContactData(
          survey.imprintLink !== '' || survey.contactEmail !== '' || survey.privacyPolicyLink !== ''
        );
        setCanEditDesign(
          survey.primaryColor !== '#357E8B' ||
            survey.secondaryColor !== '#000000' ||
            survey.companyLogo !== ''
        );
      } catch (error) {
        console.error('Failed to fetch survey:', error);
      } finally {
        setLoading(false);
      }
    };

    getSurvey();

    return () => {
      controller.abort();
    };
  }, [surveyID]);

  return (
    <>
      {!isEdit ? (
        <>
          <Box sx={{ maxWidth: '600px' }}>
            <Stepper
              activeStep={currentSurveyGuideStep}
              orientation="vertical"
            >
              <Step>
                <StepLabel>{t('survey_label')}</StepLabel>
                <StepContent>
                  <TextField
                    label={t('survey_name')}
                    placeholder={'z.B.: Musterfirma'}
                    size={'small'}
                    variant="outlined"
                    sx={{ minWidth: '400px' }}
                    autoFocus
                    value={surveyData.label}
                    onChange={(e: ChangeEvent<HTMLInputElement>) => {
                      setSurveyData((prevState) => ({
                        ...prevState,
                        label: e.target.value,
                      }));
                    }}
                  />
                  <Box sx={{ mb: theme.spacing(1) }}>
                    <Button
                      disabled={!surveyData.label}
                      variant="contained"
                      onClick={() => {
                        handleNextSurveyGuideStep();
                      }}
                      sx={{ mt: theme.spacing(2), mr: theme.spacing(1) }}
                    >
                      {t('next')}
                    </Button>
                  </Box>
                </StepContent>
              </Step>
              <Step>
                <StepLabel>{t('company_data_label')}</StepLabel>
                <StepContent>
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center',
                      minWidth: 284,
                    }}
                  >
                    <TextField
                      label={t('company_name')}
                      size={'small'}
                      variant="outlined"
                      value={surveyData.companyName}
                      placeholder={t('company_name')}
                      onChange={(event: ChangeEvent<HTMLInputElement>) => {
                        setSurveyData((prevState) => ({
                          ...prevState,
                          companyName: event.target.value,
                        }));
                      }}
                      sx={{ minWidth: '284px', mt: theme.spacing(2) }}
                    />
                    <TextField
                      label={t('enter_company_url')}
                      size="small"
                      variant="outlined"
                      placeholder={'z.B.: example.com'}
                      multiline
                      maxRows={'2'}
                      value={surveyData.companyWebsiteUrl}
                      onChange={(event: ChangeEvent<HTMLInputElement>) => {
                        setSurveyData((prevState) => ({
                          ...prevState,
                          companyWebsiteUrl: event.target.value,
                        }));
                        setShowUrlError(false);
                      }}
                      error={showUrlError}
                      helperText={showUrlError ? t('url_invalid') : ''}
                      sx={{
                        minWidth: '284px',
                        mt: theme.spacing(2),
                        color: showUrlError ? 'red' : 'inherit',
                      }}
                      fullWidth
                    />
                  </Box>
                  <Box>
                    <Button
                      disabled={
                        !surveyData.companyName || !surveyData.companyWebsiteUrl || showUrlError
                      }
                      variant="contained"
                      onClick={() => {
                        if (!isValidCompanyUrl(surveyData.companyWebsiteUrl)) {
                          setShowUrlError(true);
                          return;
                        }
                        handleNextSurveyGuideStep();
                        handleOutroTextChange(surveyData.companyName);
                      }}
                      sx={{ mt: theme.spacing(2), mr: theme.spacing(1) }}
                    >
                      {t('next')}
                    </Button>
                    <Button
                      variant="contained"
                      color="warning"
                      onClick={handlePreviousSurveyGuideStep}
                      sx={{ mt: theme.spacing(2), mr: theme.spacing(1) }}
                    >
                      {t('cancel')}
                    </Button>
                  </Box>
                </StepContent>
              </Step>
              <Step>
                <StepLabel>{'Titel'}</StepLabel>
                <StepContent>
                  <TextField
                    label={'Titel'}
                    size={'small'}
                    multiline
                    maxRows={'3'}
                    variant={'outlined'}
                    value={surveyData.title}
                    placeholder={'Bitte teilen Sie uns Ihre aktuelle Stimmung mit.'}
                    onChange={(event: ChangeEvent<HTMLInputElement>) => {
                      setSurveyData((prevState) => ({
                        ...prevState,
                        title: event.target.value,
                      }));
                    }}
                    sx={{ minWidth: '400px', mt: theme.spacing(2) }}
                  />
                  <Box sx={{ mb: theme.spacing(2) }}>
                    <Button
                      variant="contained"
                      onClick={() => {
                        handleNextSurveyGuideStep();
                      }}
                      sx={{ mt: theme.spacing(2), mr: theme.spacing(1) }}
                    >
                      {t('next')}
                    </Button>
                    <Button
                      variant="contained"
                      color="warning"
                      onClick={handlePreviousSurveyGuideStep}
                      sx={{ mt: theme.spacing(2), mr: theme.spacing(1) }}
                    >
                      {t('cancel')}
                    </Button>
                  </Box>
                </StepContent>
              </Step>
              <Step>
                <StepLabel>{'Intro'}</StepLabel>
                <StepContent>
                  <TextField
                    label={'Intro'}
                    size={'small'}
                    multiline
                    maxRows={'6'}
                    variant={'outlined'}
                    value={surveyData.introText}
                    onChange={(event: ChangeEvent<HTMLInputElement>) => {
                      setSurveyData((prevState) => ({
                        ...prevState,
                        introText: event.target.value,
                      }));
                    }}
                    sx={{ minWidth: '400px', mt: theme.spacing(2) }}
                  />
                  <Box sx={{ mb: theme.spacing(2) }}>
                    <Button
                      variant="contained"
                      onClick={() => {
                        handleNextSurveyGuideStep();
                      }}
                      sx={{ mt: theme.spacing(2), mr: theme.spacing(1) }}
                    >
                      {t('next')}
                    </Button>
                    <Button
                      variant="contained"
                      color="warning"
                      onClick={handlePreviousSurveyGuideStep}
                      sx={{ mt: theme.spacing(2), mr: theme.spacing(1) }}
                    >
                      {t('cancel')}
                    </Button>
                  </Box>
                </StepContent>
              </Step>
              <Step>
                <StepLabel>{'Outro'}</StepLabel>
                <StepContent>
                  <TextField
                    label={'Outro'}
                    size={'small'}
                    multiline
                    maxRows={'6'}
                    variant={'outlined'}
                    value={surveyData.outroText}
                    onChange={(event: ChangeEvent<HTMLInputElement>) => {
                      setSurveyData((prevState) => ({
                        ...prevState,
                        outroText: event.target.value,
                      }));
                    }}
                    sx={{ minWidth: '400px', mt: theme.spacing(2) }}
                  />
                  <Box sx={{ mb: theme.spacing(2) }}>
                    <Button
                      variant="contained"
                      onClick={() => {
                        handleNextSurveyGuideStep();
                      }}
                      sx={{ mt: theme.spacing(2), mr: theme.spacing(1) }}
                    >
                      {t('next')}
                    </Button>
                    <Button
                      variant="contained"
                      color="warning"
                      onClick={handlePreviousSurveyGuideStep}
                      sx={{ mt: theme.spacing(2), mr: theme.spacing(1) }}
                    >
                      {t('cancel')}
                    </Button>
                  </Box>
                </StepContent>
              </Step>
              <Step>
                <StepLabel>{t('finish_configuration')}</StepLabel>
                <StepContent>
                  <Box sx={{ mb: theme.spacing(2) }}>
                    <Button
                      variant="contained"
                      startIcon={<Check />}
                      sx={{ mt: theme.spacing(2), mr: theme.spacing(1) }}
                      onClick={handleAddSurvey}
                    >
                      {t('finish')}
                    </Button>
                    <Button
                      variant="contained"
                      color="warning"
                      onClick={handlePreviousSurveyGuideStep}
                      sx={{ mt: theme.spacing(2), mr: theme.spacing(1) }}
                    >
                      {t('cancel')}
                    </Button>
                  </Box>
                </StepContent>
              </Step>
            </Stepper>
          </Box>
          <Box sx={{ display: 'flex', minHeight: '300px', mx: theme.spacing(6) }}>
            <Divider
              orientation="vertical"
              flexItem
            />
          </Box>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignSelf: 'flex-start',
              minWidth: '400px',
            }}
          >
            <Typography
              variant="body2"
              sx={{ mb: theme.spacing(2) }}
            >
              {t('optional_configuration')}
            </Typography>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mt: theme.spacing(1),
              }}
            >
              <Typography>{t('language')}</Typography>
              <ToggleButtonGroup
                color="primary"
                size="small"
                value={surveyData.lang}
                exclusive
                onChange={(event: MouseEvent<HTMLElement>, value: string) => {
                  if (value) {
                    setSurveyData((prevState) => ({
                      ...prevState,
                      lang: value,
                    }));
                  }
                }}
                sx={{
                  '& .Mui-selected.MuiToggleButtonGroup-grouped': {
                    border: `1px solid ${theme.palette.primary.main}`,
                  },
                }}
              >
                <ToggleButton value="de">
                  <FlagDe
                    width="32px"
                    height="100%"
                  />
                </ToggleButton>
                <ToggleButton value="en">
                  <FlagGb
                    width="32px"
                    height="100%"
                  />
                </ToggleButton>
                <ToggleButton value="nl">
                  <FlagNl
                    width="32px"
                    height="100%"
                  />
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>
            <FormGroup>
              <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={canEditDesign}
                      onChange={() => {
                        setCanEditDesign((prevState) => !prevState);
                      }}
                    />
                  }
                  label={<Typography variant="body2">Design</Typography>}
                />
                {canEditDesign && (
                  <DesignChanger
                    defaultDesign={design}
                    onChange={setDesign}
                  />
                )}
                <FormControlLabel
                  sx={{ mt: theme.spacing(2) }}
                  control={
                    <Switch
                      checked={showContactData}
                      onChange={() => {
                        setShowContactData((prevState) => !prevState);
                      }}
                    />
                  }
                  label={<Typography variant="body2">Kontaktdaten</Typography>}
                />
              </Box>
            </FormGroup>
            {showContactData && (
              <SurveyContactData
                privacyPolicyLink={contactData.privacyPolicyLink}
                contactEmail={contactData.contactEmail}
                imprintLink={contactData.imprintLink}
                contactForm={showContactData}
                onChange={setContactData}
              />
            )}
          </Box>
        </>
      ) : (
        <>
          <Box sx={{ maxWidth: '600px' }}>
            <Stepper
              activeStep={currentSurveyGuideStep}
              orientation="vertical"
            >
              <Step>
                <StepLabel>{t('survey_label')}</StepLabel>
                <StepContent>
                  <TextField
                    label={t('survey_name')}
                    placeholder={'z.B.: Musterfirma'}
                    size={'small'}
                    variant="outlined"
                    sx={{ minWidth: '400px' }}
                    autoFocus
                    value={surveyData.label}
                    onChange={(e: ChangeEvent<HTMLInputElement>) => {
                      setSurveyData((prevState) => ({
                        ...prevState,
                        label: e.target.value,
                      }));
                    }}
                  />
                  <Box sx={{ mb: theme.spacing(1) }}>
                    <Button
                      disabled={!surveyData.label}
                      variant="contained"
                      onClick={() => {
                        handleNextSurveyGuideStep();
                      }}
                      sx={{ mt: theme.spacing(2), mr: theme.spacing(1) }}
                    >
                      {t('next')}
                    </Button>
                  </Box>
                </StepContent>
              </Step>
              <Step>
                <StepLabel>{t('finish_configuration')}</StepLabel>
                <StepContent>
                  <Box sx={{ mb: theme.spacing(2) }}>
                    <Button
                      variant="contained"
                      startIcon={<Check />}
                      sx={{ mt: theme.spacing(2), mr: theme.spacing(1) }}
                      onClick={handleAddSurvey}
                    >
                      {t('finish')}
                    </Button>
                    <Button
                      variant="contained"
                      color="warning"
                      onClick={handlePreviousSurveyGuideStep}
                      sx={{ mt: theme.spacing(2), mr: theme.spacing(1) }}
                    >
                      {t('cancel')}
                    </Button>
                  </Box>
                </StepContent>
              </Step>
            </Stepper>
          </Box>
          <Box sx={{ display: 'flex', minHeight: '300px', mx: theme.spacing(6) }}>
            <Divider
              orientation="vertical"
              flexItem
            />
          </Box>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignSelf: 'flex-start',
              minWidth: '400px',
            }}
          >
            <Typography
              variant="body2"
              sx={{ mb: theme.spacing(2) }}
            >
              {t('optional_configuration')}
            </Typography>
            <FormGroup>
              <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mt: theme.spacing(1),
                  }}
                >
                  <Typography>{t('language')}</Typography>
                  <ToggleButtonGroup
                    color="primary"
                    size="small"
                    value={surveyData.lang}
                    exclusive
                    onChange={(event: MouseEvent<HTMLElement>, value: string | null) => {
                      if (value) {
                        setSurveyData((prevState) => ({
                          ...prevState,
                          lang: value,
                        }));
                      }
                    }}
                    sx={{
                      '& .Mui-selected.MuiToggleButtonGroup-grouped': {
                        border: `1px solid ${theme.palette.primary.main}`,
                      },
                    }}
                  >
                    <ToggleButton value="de">
                      <FlagDe
                        width="32px"
                        height="100%"
                      />
                    </ToggleButton>
                    <ToggleButton value="en">
                      <FlagGb
                        width="32px"
                        height="100%"
                      />
                    </ToggleButton>
                    <ToggleButton value="nl">
                      <FlagNl
                        width="32px"
                        height="100%"
                      />
                    </ToggleButton>
                  </ToggleButtonGroup>
                </Box>
                <FormControlLabel
                  control={
                    <Switch
                      checked={canEditDesign}
                      onChange={() => {
                        setCanEditDesign((prevState) => !prevState);
                      }}
                    />
                  }
                  label={<Typography variant="body2">Design</Typography>}
                />

                {canEditDesign && (
                  <DesignChanger
                    defaultDesign={design}
                    onChange={setDesign}
                  />
                )}
                <FormControlLabel
                  sx={{ mt: theme.spacing(2) }}
                  control={
                    <Switch
                      checked={showContactData}
                      onChange={() => {
                        setShowContactData((prevState) => !prevState);
                      }}
                    />
                  }
                  label={<Typography variant="body2">{t('contact_data')}</Typography>}
                />
              </Box>
            </FormGroup>
            {showContactData && (
              <SurveyContactData
                privacyPolicyLink={contactData.privacyPolicyLink}
                contactEmail={contactData.contactEmail}
                imprintLink={contactData.imprintLink}
                contactForm={showContactData}
                onChange={setContactData}
              />
            )}
          </Box>
        </>
      )}
    </>
  );
};

export default CustomSurveyGuid;
